/** Closed on purpose: a free-text unit cannot be added up, a free-text allergen cannot be
 * checked. */

export const MENU_MEALS = ["breakfast", "lunch", "dinner", "snack"] as const;
export type MenuMeal = (typeof MENU_MEALS)[number];

export const MENU_MEAL_LABELS: Record<MenuMeal, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

/** The most dishes one meal holds: a kitchen's plate, sides, bread, dessert and drink, not a
    buffet; a snack is one or two things. */
export const MENU_MEAL_MAX_DISHES: Record<MenuMeal, number> = {
  breakfast: 6,
  lunch: 7,
  dinner: 7,
  snack: 2,
};

/** When a meal's food comes off the shelf on the kitchen's clock, prep included: a count a person
    makes after it already holds that meal's use. */
export const MENU_MEAL_PULLED_AT: Record<MenuMeal, string> = {
  breakfast: "06:00",
  lunch: "10:00",
  snack: "14:00",
  dinner: "15:00",
};

/** Weight and volume convert; `each` and `dozen` count together, the rest only with themselves. */
export const MENU_UNITS = [
  "g",
  "kg",
  "oz",
  "lb",
  "ml",
  "l",
  "tsp",
  "tbsp",
  "cup",
  "fl oz",
  "pt",
  "qt",
  "gal",
  "each",
  "dozen",
  "slice",
  "can",
  "bag",
  "loaf",
  "head",
  "bunch",
  "clove",
] as const;
export type MenuUnit = (typeof MENU_UNITS)[number];

/** Why a pantry row changed; `order_surplus` and `menu_used` also name older orders that took
    their list's use off when they were approved. */
export type MenuPantryReason =
  | "order_delivery"
  | "order_surplus"
  | "set"
  | "menu_used"
  | "menu_day"
  | "store_delivery";

/** Base amounts, exact: US units are defined in metric, so a gallon is 128 fl oz with no
 * rounding. */
const FL_OZ_ML = 29.573_529_562_5;
const OZ_G = 28.349_523_125;

export const MENU_UNIT_MEASURE: Readonly<Record<MenuUnit, { dimension: string; factor: number }>> =
  {
    g: { dimension: "g", factor: 1 },
    kg: { dimension: "g", factor: 1000 },
    oz: { dimension: "g", factor: OZ_G },
    lb: { dimension: "g", factor: OZ_G * 16 },
    ml: { dimension: "ml", factor: 1 },
    l: { dimension: "ml", factor: 1000 },
    tsp: { dimension: "ml", factor: FL_OZ_ML / 6 },
    tbsp: { dimension: "ml", factor: FL_OZ_ML / 2 },
    cup: { dimension: "ml", factor: FL_OZ_ML * 8 },
    "fl oz": { dimension: "ml", factor: FL_OZ_ML },
    pt: { dimension: "ml", factor: FL_OZ_ML * 16 },
    qt: { dimension: "ml", factor: FL_OZ_ML * 32 },
    gal: { dimension: "ml", factor: FL_OZ_ML * 128 },
    each: { dimension: "each", factor: 1 },
    dozen: { dimension: "each", factor: 12 },
    slice: { dimension: "slice", factor: 1 },
    can: { dimension: "can", factor: 1 },
    bag: { dimension: "bag", factor: 1 },
    loaf: { dimension: "loaf", factor: 1 },
    head: { dimension: "head", factor: 1 },
    bunch: { dimension: "bunch", factor: 1 },
    clove: { dimension: "clove", factor: 1 },
  };

const EIGHTHS = ["", "⅛", "¼", "⅜", "½", "⅝", "¾", "⅞"] as const;

/** To the nearest eighth, as a measuring cup or spoon reads: 1 ½, ¾. */
export function menuEighths(value: number): string {
  const count = Math.max(1, Math.round(value * 8));
  const whole = Math.floor(count / 8);
  const part = EIGHTHS[count % 8] ?? "";
  if (!whole) return part;
  return part ? `${String(whole)} ${part}` : String(whole);
}

/** Matching only, never shown: case, accents and punctuation folded, the last word singular. */
export function menuItemKey(name: string): string {
  const words = name
    .normalize("NFKD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/['‘’ʼ]/gu, "")
    .split(/[^a-z0-9]+/u)
    .filter(Boolean);
  const last = words.at(-1);
  if (last && last.length > 3 && !/(?:ss|us|is)$/u.test(last)) {
    // "berries" is a berry, but "pies" and "ties" only drop their s.
    words[words.length - 1] = last
      .replace(/(?<=[a-z]{2})ies$/u, "y")
      .replace(/oes$/u, "o")
      .replace(/(ch|sh|x)es$/u, "$1")
      .replace(/s$/u, "");
  }
  return words.join(" ");
}

/** One food, one key: a USDA-listed ingredient by its id, anything else by its name. */
export const menuLineKey = (name: string, ingredientId?: string | null): string =>
  ingredientId ? `usda:${ingredientId}` : menuItemKey(name);

/** The nine major food allergens (FALCPA and the FASTER Act). */
export const MENU_ALLERGENS = [
  "milk",
  "egg",
  "fish",
  "shellfish",
  "tree_nuts",
  "peanuts",
  "wheat",
  "soy",
  "sesame",
] as const;
export type MenuAllergen = (typeof MENU_ALLERGENS)[number];

export const MENU_ALLERGEN_LABELS: Record<MenuAllergen, string> = {
  milk: "Milk",
  egg: "Egg",
  fish: "Fish",
  shellfish: "Shellfish",
  tree_nuts: "Tree nuts",
  peanuts: "Peanuts",
  wheat: "Wheat",
  soy: "Soy",
  sesame: "Sesame",
};

/** The longest stretch one shopping list covers, in days. */
export const MENU_LIST_MAX_DAYS = 14;

/** The most days or meals one day's menu, or one of its meals, is copied onto at once: a month. */
export const MENU_COPY_MAX_TARGETS = 31;
